import { useCallback, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/use-auth";
import { getLocale, setLocale, t } from "../i18n";

interface NavChild {
  labelKey: string;
  path: string;
}

interface NavItem {
  labelKey: string;
  path?: string;
  children?: NavChild[];
}

const NAV_ITEMS: NavItem[] = [
  { labelKey: "nav.dashboard", path: "/" },
  { labelKey: "nav.metrics", path: "/metrics" },
  { labelKey: "nav.integrations", path: "/integrations" },
  {
    labelKey: "nav.users",
    children: [{ labelKey: "nav.users.all", path: "/users" }],
  },
  {
    labelKey: "nav.content",
    children: [
      { labelKey: "nav.content.conversations", path: "/content/conversations" },
      { labelKey: "nav.content.storage", path: "/content/storage" },
      { labelKey: "nav.content.summary_jobs", path: "/content/summary-jobs" },
      { labelKey: "nav.content.deliveries", path: "/content/deliveries" },
    ],
  },
  {
    labelKey: "nav.revenue",
    children: [{ labelKey: "nav.revenue.billing", path: "/revenue/billing" }],
  },
  {
    labelKey: "nav.governance",
    children: [
      { labelKey: "nav.governance.relations", path: "/governance/relations" },
      { labelKey: "nav.governance.blocks", path: "/governance/blocks" },
      { labelKey: "nav.governance.agents", path: "/governance/agents" },
      { labelKey: "nav.governance.grants", path: "/governance/grants" },
      { labelKey: "nav.governance.audit_log", path: "/governance/audit" },
    ],
  },
  {
    labelKey: "nav.invites",
    children: [
      { labelKey: "nav.invites.applications", path: "/invites/applications" },
      { labelKey: "nav.invites.codes", path: "/invites/codes" },
    ],
  },
  {
    labelKey: "nav.system",
    children: [
      { labelKey: "nav.system.health", path: "/system/health" },
      { labelKey: "nav.system.crashes", path: "/system/crashes" },
    ],
  },
];

function isGroupActive(
  children: NavChild[] | undefined,
  pathname: string,
): boolean {
  if (!children) return false;
  return children.some((c) => pathname.startsWith(c.path));
}

export function Sidebar(): React.JSX.Element {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const item of NAV_ITEMS) {
      if (item.children && isGroupActive(item.children, location.pathname)) {
        initial[item.labelKey] = true;
      }
    }
    return initial;
  });

  const toggle = useCallback((key: string): void => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return (
    <aside style={styles.sidebar}>
      <div style={styles.logo}>{t("sidebar.logo")}</div>

      <nav style={styles.nav}>
        {NAV_ITEMS.map((item) => (
          <NavGroup
            key={item.labelKey}
            item={item}
            isExpanded={!!expanded[item.labelKey]}
            onToggle={toggle}
          />
        ))}
      </nav>

      <div style={styles.footer}>
        <div style={styles.userEmail}>{user?.email ?? ""}</div>
        <button type="button" onClick={logout} style={styles.logoutBtn}>
          {t("sidebar.sign_out")}
        </button>
        <button
          type="button"
          onClick={() => setLocale(getLocale() === "en" ? "zh" : "en")}
          style={styles.localeBtn}
        >
          {getLocale() === "en" ? "\u4E2D\u6587" : "English"}
        </button>
      </div>
    </aside>
  );
}

function NavGroup({
  item,
  isExpanded,
  onToggle,
}: {
  item: NavItem;
  isExpanded: boolean;
  onToggle: (label: string) => void;
}): React.JSX.Element {
  if (item.path !== undefined) {
    return (
      <NavLink
        to={item.path}
        end
        style={({ isActive }) => ({
          ...styles.navLink,
          ...(isActive ? styles.navLinkActive : {}),
        })}
      >
        {t(item.labelKey)}
      </NavLink>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(item.labelKey)}
        style={styles.navGroupBtn}
      >
        <span>{t(item.labelKey)}</span>
        <span style={styles.chevron}>{isExpanded ? "\u25B4" : "\u25BE"}</span>
      </button>
      {isExpanded && item.children && (
        <div style={styles.navChildren}>
          {item.children.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              style={({ isActive }) => ({
                ...styles.navChildLink,
                ...(isActive ? styles.navLinkActive : {}),
              })}
            >
              {t(child.labelKey)}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  sidebar: {
    width: "var(--sidebar-width)",
    height: "100vh",
    position: "fixed",
    top: 0,
    left: 0,
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
  logo: {
    height: "var(--header-height)",
    display: "flex",
    alignItems: "center",
    padding: "0 20px",
    fontWeight: 700,
    fontSize: 16,
    borderBottom: "1px solid var(--color-border)",
    letterSpacing: "-0.02em",
  },
  nav: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 0",
  },
  navLink: {
    display: "block",
    padding: "8px 20px",
    fontSize: 14,
    color: "var(--color-text)",
    textDecoration: "none",
    borderRadius: 0,
  },
  navLinkActive: {
    background: "var(--color-bg-hover)",
    fontWeight: 600,
  },
  navGroupBtn: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 20px",
    fontSize: 11,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    fontWeight: 600,
  },
  chevron: {
    fontSize: 10,
  },
  navChildren: {
    paddingLeft: 12,
  },
  navChildLink: {
    display: "block",
    padding: "6px 20px",
    fontSize: 14,
    color: "var(--color-text)",
    textDecoration: "none",
  },
  footer: {
    padding: "12px 20px",
    borderTop: "1px solid var(--color-border)",
  },
  userEmail: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    marginBottom: 8,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  logoutBtn: {
    fontSize: 13,
    color: "var(--color-danger)",
    padding: 0,
  },
  localeBtn: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    padding: "4px 0",
    marginTop: 4,
    background: "none",
    border: "none",
    cursor: "pointer",
    textAlign: "left" as const,
  },
};
